type JsonObject = Record<string, unknown>

const DEEPGRAM_DEFAULT_LISTEN_MODEL = "nova-3"
const DEEPGRAM_DEFAULT_THINK_PROVIDER = "open_ai"
const DEEPGRAM_DEFAULT_THINK_MODEL = "gpt-4o-mini"
const DEEPGRAM_DEFAULT_SPEAK_MODEL = "flux-kit-en"

export const DEEPGRAM_LISTEN_MODEL_SUGGESTIONS = [
  "nova-3",
  "nova-2",
  "flux-general-en",
  "flux-general-multi",
] as const

export const DEEPGRAM_SPEAK_MODEL_SUGGESTIONS = [
  "flux-kit-en",
  "flux-alexis-en",
  "aura-2-thalia-en",
  "aura-asteria-en",
] as const

export const DEEPGRAM_THINK_PROVIDERS = [
  {
    value: "open_ai",
    label: "OpenAI",
    defaultModel: "gpt-4o-mini",
    models: [
      "gpt-5.6-luna",
      "gpt-5.6-terra",
      "gpt-5.4-mini",
      "gpt-4.1-mini",
      "gpt-4o-mini",
    ],
  },
  {
    value: "anthropic",
    label: "Anthropic",
    defaultModel: "claude-haiku-4-5",
    models: [
      "claude-haiku-4-5",
      "claude-sonnet-4-5",
      "claude-sonnet-4-6",
      "claude-sonnet-5",
    ],
  },
  {
    value: "google",
    label: "Google",
    defaultModel: "gemini-2.5-flash",
    models: [
      "gemini-2.5-flash",
      "gemini-3-flash-preview",
      "gemini-3.1-flash-lite",
      "gemini-3.5-flash",
    ],
  },
  {
    value: "nvidia",
    label: "NVIDIA",
    defaultModel: "nvidia/nemotron-3-nano-30b-a3b",
    models: ["nvidia/nemotron-3-nano-30b-a3b"],
  },
] as const

interface DeepgramGuidedSettings {
  listenModel: string
  thinkProvider: string
  thinkModel: string
  speakModel: string
  greeting: string
  editable: boolean
}

export type DeepgramGuidedSetting =
  | "listenModel"
  | "thinkProvider"
  | "thinkModel"
  | "speakModel"
  | "greeting"

const isJsonObject = (value: unknown): value is JsonObject =>
  typeof value === "object" && value !== null && !Array.isArray(value)

const parseSettingsObject = (
  settings: string | undefined,
): JsonObject | null => {
  if (!settings?.trim()) {
    return {}
  }

  try {
    const parsed: unknown = JSON.parse(settings)
    return isJsonObject(parsed) ? parsed : null
  } catch {
    return null
  }
}

const getObject = (parent: JsonObject, key: string): JsonObject | null => {
  const value = parent[key]
  if (value === undefined) {
    return {}
  }
  return isJsonObject(value) ? value : null
}

const getProvider = (section: JsonObject): JsonObject | null =>
  getObject(section, "provider")

const readString = (object: JsonObject, key: string, fallback: string) =>
  typeof object[key] === "string" ? object[key] : fallback

export function getDeepgramGuidedSettings(
  settings: string | undefined,
): DeepgramGuidedSettings {
  const root = parseSettingsObject(settings)
  if (!root) {
    return {
      listenModel: DEEPGRAM_DEFAULT_LISTEN_MODEL,
      thinkProvider: DEEPGRAM_DEFAULT_THINK_PROVIDER,
      thinkModel: DEEPGRAM_DEFAULT_THINK_MODEL,
      speakModel: DEEPGRAM_DEFAULT_SPEAK_MODEL,
      greeting: "",
      editable: false,
    }
  }

  const agent = getObject(root, "agent")
  const listen = agent ? getObject(agent, "listen") : null
  const think = agent ? getObject(agent, "think") : null
  const speak = agent ? getObject(agent, "speak") : null
  const listenProvider = listen ? getProvider(listen) : null
  const thinkProvider = think ? getProvider(think) : null
  const speakProvider = speak ? getProvider(speak) : null
  const thinkProviderType = thinkProvider
    ? readString(thinkProvider, "type", DEEPGRAM_DEFAULT_THINK_PROVIDER)
    : DEEPGRAM_DEFAULT_THINK_PROVIDER
  const speakProviderType = speakProvider?.type
  const usesManagedThinkProvider = DEEPGRAM_THINK_PROVIDERS.some(
    (provider) => provider.value === thinkProviderType,
  )

  const editable =
    !!agent &&
    !!listen &&
    !!think &&
    !!speak &&
    !!listenProvider &&
    !!thinkProvider &&
    !!speakProvider &&
    usesManagedThinkProvider &&
    (speakProviderType === undefined || speakProviderType === "deepgram")

  return {
    listenModel: listenProvider
      ? readString(listenProvider, "model", DEEPGRAM_DEFAULT_LISTEN_MODEL)
      : DEEPGRAM_DEFAULT_LISTEN_MODEL,
    thinkProvider: thinkProviderType,
    thinkModel: thinkProvider
      ? readString(thinkProvider, "model", DEEPGRAM_DEFAULT_THINK_MODEL)
      : DEEPGRAM_DEFAULT_THINK_MODEL,
    speakModel: speakProvider
      ? readString(speakProvider, "model", DEEPGRAM_DEFAULT_SPEAK_MODEL)
      : DEEPGRAM_DEFAULT_SPEAK_MODEL,
    greeting: agent ? readString(agent, "greeting", "") : "",
    editable,
  }
}

const ensureObject = (parent: JsonObject, key: string): JsonObject => {
  const existing = parent[key]
  if (isJsonObject(existing)) {
    return existing
  }
  const created: JsonObject = {}
  parent[key] = created
  return created
}

const versionForModel = (model: string) =>
  model.startsWith("flux-") ? "v2" : "v1"

export function updateDeepgramGuidedSetting(
  settings: string | undefined,
  field: DeepgramGuidedSetting,
  value: string,
): string {
  const root = parseSettingsObject(settings)
  if (!root || !getDeepgramGuidedSettings(settings).editable) {
    return settings ?? ""
  }

  const agent = ensureObject(root, "agent")
  if (field === "greeting") {
    if (value.trim()) {
      agent.greeting = value
    } else {
      delete agent.greeting
    }
    return JSON.stringify(root, null, 2)
  }

  if (field === "listenModel") {
    const provider = ensureObject(ensureObject(agent, "listen"), "provider")
    provider.type = "deepgram"
    provider.model = value
    provider.version = versionForModel(value)
  }

  if (field === "thinkProvider") {
    const selectedProvider = DEEPGRAM_THINK_PROVIDERS.find(
      (provider) => provider.value === value,
    )
    if (!selectedProvider) {
      return settings ?? ""
    }
    const provider = ensureObject(ensureObject(agent, "think"), "provider")
    provider.type = value
    provider.model = selectedProvider.defaultModel
  }

  if (field === "thinkModel") {
    const provider = ensureObject(ensureObject(agent, "think"), "provider")
    provider.model = value
  }

  if (field === "speakModel") {
    const provider = ensureObject(ensureObject(agent, "speak"), "provider")
    provider.type = "deepgram"
    provider.model = value
    provider.version = versionForModel(value)
  }

  return JSON.stringify(root, null, 2)
}
